import { useCallback, useState } from 'react';

import { Trans } from '@lyearn/i18n';
import { CheckmarkCircleFill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import {
  RelatedEntityTypeEnum,
  TaskAvailableOnEnum,
  TaskSelectionConfigInput,
  TaskSelectionOperationEnum,
} from '@/types/schema';

import { useUpdateEntitiesTasksRelationMutation } from '../graphql/mutations/generated/updateEntitiesTasksRelation';

interface UseUpdateEntitiesProps {
  entityId?: string;
  entityType?: RelatedEntityTypeEnum;
}

const MAPPED_ACTION_ENTITY_TO_TASK = {
  [RelatedEntityTypeEnum.Okr]: TaskAvailableOnEnum.Okr,
  [RelatedEntityTypeEnum.KeyResult]: TaskAvailableOnEnum.KeyResult,
  [RelatedEntityTypeEnum.MeetingInstance]: TaskAvailableOnEnum.MeetingInstance,
  [RelatedEntityTypeEnum.AgendaItem]: TaskAvailableOnEnum.AgendaItem,
  [RelatedEntityTypeEnum.TeamMeeting]: TaskAvailableOnEnum.AgendaItem,
  [RelatedEntityTypeEnum.PeerFeedback]: TaskAvailableOnEnum.PeerFeedback,
  // for ts
  [RelatedEntityTypeEnum.Meeting]: TaskAvailableOnEnum.AgendaItem,
  [RelatedEntityTypeEnum.ActionPlan]: TaskAvailableOnEnum.AgendaItem,
};

export default function useUpdateEntities(props: UseUpdateEntitiesProps) {
  const { entityId, entityType } = props;
  const [, updateEntitiesTasksRelation] = useUpdateEntitiesTasksRelationMutation();
  const { handleResponse } = useResponse();
  const { showSnackbar } = useSnackbar();
  const [isFetching, setIsFetching] = useState(false);

  const onAddEntities = useCallback(
    async (
      taskSelectionConfig: TaskSelectionConfigInput,
      selectedRootTasksCount: number,
      onClose: () => void,
    ) => {
      if (!entityId || !entityType) {
        return;
      }
      setIsFetching(true);

      const data = await updateEntitiesTasksRelation({
        input: {
          entityTasksRelations: [
            {
              entityId,
              entityType: MAPPED_ACTION_ENTITY_TO_TASK[entityType],
              taskSelectionConfig,
            },
          ],
        },
      });

      handleResponse({
        condition: !!data.data?.updateEntitiesTasksRelation?.success,
        onSuccess: () => {
          showSnackbar({
            startIcon: <CheckmarkCircleFill fillColorToken="icon-success-light" />,
            message: (
              <Trans i18nKey="Task:TaskAddedSnackbar" values={{ count: selectedRootTasksCount }} />
            ),
          });
          onClose();
          setIsFetching(false);
        },
      });
    },
    [entityId, entityType, handleResponse, showSnackbar, updateEntitiesTasksRelation],
  );

  const onRemoveEntities = useCallback(
    async (taskIdsToRemove: string[]) => {
      if (!entityId || !entityType || !taskIdsToRemove.length) {
        return;
      }
      setIsFetching(true);

      const data = await updateEntitiesTasksRelation({
        input: {
          entityTasksRelations: [
            {
              entityId,
              entityType: MAPPED_ACTION_ENTITY_TO_TASK[entityType],
              taskSelectionConfig: {
                operation: TaskSelectionOperationEnum.Remove,
                includeTaskIds: taskIdsToRemove,
              },
            },
          ],
        },
      });

      handleResponse({
        condition: !!data.data?.updateEntitiesTasksRelation?.success,
        onSuccess: () => {
          showSnackbar({
            startIcon: <CheckmarkCircleFill fillColorToken="icon-success-light" />,
            message: <Trans i18nKey="Task:TaskWasRemoved" />,
          });
          setIsFetching(false);
        },
      });
    },
    [entityId, entityType, handleResponse, showSnackbar, updateEntitiesTasksRelation],
  );

  return {
    isFetching,
    onAddEntities,
    onRemoveEntities,
  };
}
